import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, User, Star, FileText, Paperclip, CalendarClock, Upload, X } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { useTicket, useCloseTicketWithCsat } from '../../hooks/use-tickets';
import { STATUS_LABELS, STATUS_COLORS, PRIORITY_LABELS, PRIORITY_COLORS, PRIORITY_DESCRIPTIONS, formatTicketId } from '../../lib/constants';
import { formatDateTime } from '../../lib/utils';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api-client';
import type { Comment } from '../../types';
import { useState } from 'react';
import { AttachmentPreview } from '../../components/ui/AttachmentPreview';
import { TicketProgress } from '../../components/ui/TicketProgress';
import { DevelopmentLogTable } from '../../components/DevelopmentLogTable';
import { useAuthStore } from '../../stores/auth-store';

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useTicket(id!);
  const ticket = data?.data as (NonNullable<typeof data>['data'] & { contractName?: string; projectName?: string; clientName?: string }) | undefined;
  const qc = useQueryClient();
  const closeWithCsat = useCloseTicketWithCsat();
  const currentUser = useAuthStore((s) => s.user);

  const { data: commentsData } = useQuery({
    queryKey: ['comments', id],
    queryFn: () => api.get<Comment[]>(`/tickets/${id}/comments`),
    enabled: !!id,
  });
  const comments = commentsData?.data ?? [];

  const [newComment, setNewComment] = useState('');
  const [commentFiles, setCommentFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [notResolved, setNotResolved] = useState(false);
  const addComment = useMutation({
    mutationFn: async () => {
      await api.post(`/tickets/${id}/comments`, { body: newComment, notResolved });
      // Upload attached files
      if (commentFiles.length > 0) {
        setUploading(true);
        for (const file of commentFiles) {
          const fd = new FormData();
          fd.append('ticketId', id!);
          fd.append('file', file);
          await api.upload('/attachments', fd);
        }
        setUploading(false);
      }
    },
    onSuccess: () => {
      setNewComment('');
      setCommentFiles([]);
      setNotResolved(false);
      qc.invalidateQueries({ queryKey: ['comments', id] });
      qc.invalidateQueries({ queryKey: ['tickets', id] });
    },
  });

  const [csatScore, setCsatScore] = useState(0);
  const [csatComment, setCsatComment] = useState('');

  if (isLoading) {
    return <div className="space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-40 w-full" /></div>;
  }

  if (!ticket) {
    return <p className="text-center text-gray-500">Tichetul nu a fost găsit</p>;
  }

  const handleCsatSubmit = () => {
    if (csatScore < 1) return;
    closeWithCsat.mutate(
      { id: id!, score: csatScore, comment: csatComment || undefined },
      { onError: (err) => { alert(err instanceof Error ? err.message : 'Eroare la trimiterea feedback-ului'); } },
    );
  };

  return (
    <div className="space-y-6">
      <Link to="/tickets" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-dark-muted dark:hover:text-dark-text">
        <ArrowLeft size={16} /> Înapoi la tichete
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-500 dark:bg-dark-border dark:text-dark-muted">#{formatTicketId(ticket.numericId)}</span>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text">{ticket.subject}</h1>
          </div>
          <div className="mt-1 flex items-center gap-3 text-sm text-gray-500 dark:text-dark-muted">
            <span className="flex items-center gap-1"><User size={14} /> {ticket.createdBy?.name ?? '—'}</span>
            <span className="flex items-center gap-1"><Clock size={14} /> {formatDateTime(ticket.createdAt)}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex gap-2">
            <Badge className={STATUS_COLORS[ticket.status]}>{STATUS_LABELS[ticket.status]}</Badge>
            <Badge className={PRIORITY_COLORS[ticket.priority]}>{PRIORITY_LABELS[ticket.priority]}</Badge>
          </div>
          <p className="text-xs text-gray-500 dark:text-dark-muted italic max-w-xs text-right">{PRIORITY_DESCRIPTIONS[ticket.priority]}</p>
        </div>
      </div>

      {/* Ticket progress breadcrumb */}
      <Card>
        <CardContent>
          <TicketProgress currentStatus={ticket.status} statusHistory={ticket.statusHistory} view="client" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><h2 className="font-semibold">Descriere</h2></CardHeader>
        <CardContent><p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-dark-text">{ticket.description}</p></CardContent>
      </Card>

      {/* Contract / Project / Attachments */}
      <Card>
        <CardHeader><h2 className="font-semibold flex items-center gap-2"><FileText size={16} /> Contract & Proiect</h2></CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-dark-muted">Contract</span>
            <span className="font-medium dark:text-dark-text">{ticket.contractName ?? ticket.projectId ?? '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-dark-muted">Proiect / Subcategorie</span>
            <span className="font-medium dark:text-dark-text">{ticket.projectName ?? ticket.subcategoryId ?? '—'}</span>
          </div>
        </CardContent>
      </Card>

      {ticket.attachments && ticket.attachments.length > 0 && (() => {
        const clientAtts = ticket.attachments!.filter((a) => a.uploadedBy?.role === 'CLIENT');
        const encorsaAtts = ticket.attachments!.filter((a) => a.uploadedBy?.role !== 'CLIENT');
        return (
          <Card>
            <CardHeader><h2 className="font-semibold flex items-center gap-2"><Paperclip size={16} /> Atașamente ({ticket.attachments!.length})</h2></CardHeader>
            <CardContent className="space-y-4">
              {clientAtts.length > 0 && (
                <div>
                  <h3 className="mb-2 text-xs font-semibold uppercase text-gray-500 dark:text-dark-muted">Adăugate de client</h3>
                  <div className="space-y-2">
                    {clientAtts.map((att) => (
                      <AttachmentPreview key={att.id} attachment={att} />
                    ))}
                  </div>
                </div>
              )}
              {encorsaAtts.length > 0 && (
                <div>
                  <h3 className="mb-2 text-xs font-semibold uppercase text-gray-500 dark:text-dark-muted">Adăugate de Encorsa</h3>
                  <div className="space-y-2">
                    {encorsaAtts.map((att) => (
                      <AttachmentPreview key={att.id} attachment={att} />
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {ticket.assignedTo && (
        <Card>
          <CardContent className="flex items-center gap-2 text-sm">
            <User size={16} className="text-gray-400 dark:text-dark-muted" />
            <span className="text-gray-500 dark:text-dark-muted">Atribuit lui:</span>
            <span className="font-medium dark:text-dark-text">{ticket.assignedTo.name}</span>
          </CardContent>
        </Card>
      )}

      {/* Estimation (read-only for client, shown after ESTIMATING) */}
      {(ticket.estimatedTime != null || ticket.developmentDuration != null || ticket.developmentDescription != null) && (
        <Card>
          <CardHeader><h2 className="font-semibold flex items-center gap-2"><CalendarClock size={16} /> Estimare</h2></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-dark-muted">Ore estimate de lucru</span>
              <span className="dark:text-dark-text">{ticket.estimatedTime ?? '—'}</span>
            </div>
            {(ticket.status === 'RESOLVED' || ticket.status === 'CLOSED') && (
              <DevelopmentLogTable ticketId={id!} editable={false} total={ticket.developmentDuration} />
            )}
            {(ticket.status === 'RESOLVED' || ticket.status === 'CLOSED') && ticket.developmentDescription != null && (
              <div>
                <span className="text-gray-500 dark:text-dark-muted">Descriere dezvoltare</span>
                <p className="mt-1 whitespace-pre-wrap dark:text-dark-text">{ticket.developmentDescription}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Comments */}
      <Card>
        <CardHeader><h2 className="font-semibold">Comentarii ({comments.length})</h2></CardHeader>
        <div className="divide-y">
          {comments.map((c) => (
            <div key={c.id} className="px-6 py-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium dark:text-dark-text">{c.author.name}</span>
                <span className="text-xs text-gray-400 dark:text-dark-muted">{formatDateTime(c.createdAt)}</span>
              </div>
              <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700 dark:text-dark-text">{c.body}</p>
            </div>
          ))}
          {comments.length === 0 && (
            <p className="px-6 py-4 text-sm text-gray-400 dark:text-dark-muted">Niciun comentariu</p>
          )}
        </div>
        {ticket.status === 'CLOSED' ? (
          <div className="border-t px-6 py-4">
            <p className="text-sm text-gray-500 dark:text-dark-muted">Ticketul este închis. Redeschideți pentru a adăuga comentarii.</p>
          </div>
        ) : (
        <div className="border-t px-6 py-4 space-y-2">
          <Textarea placeholder="Adaugă un comentariu..." value={newComment} onChange={(e) => setNewComment(e.target.value)} />
          {ticket.status === 'RESOLVED' && currentUser?.id === ticket.createdById && (
            <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
              <input
                type="checkbox"
                checked={notResolved}
                onChange={(e) => setNotResolved(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-amber-600"
              />
              <span>
                <span className="font-semibold">Nu e rezolvat corect</span>
                <span className="block text-xs opacity-80">Trimite tichetul înapoi în lucru. Completează comentariul cu ce nu e în regulă.</span>
              </span>
            </label>
          )}
          <div>
            <label className="inline-flex items-center gap-1.5 cursor-pointer rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 dark:text-dark-muted dark:hover:bg-dark-border">
              <Upload size={14} />
              Atașează fișiere
              <input
                type="file"
                multiple
                accept=".png,.jpg,.jpeg,.heic,.heif,.pdf,.doc,.docx,.xls,.xlsx,.mp4,.webm,.mov,.avi"
                className="hidden"
                onChange={(e) => setCommentFiles((prev) => [...prev, ...Array.from(e.target.files ?? [])])}
              />
            </label>
            {commentFiles.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {commentFiles.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-0.5 text-xs dark:bg-dark-border dark:text-dark-text">
                    {f.name}
                    <button type="button" onClick={() => setCommentFiles((prev) => prev.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500 dark:text-dark-muted">
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <Button size="sm" disabled={!newComment.trim() || addComment.isPending || uploading} onClick={() => addComment.mutate()}>
              {uploading ? 'Se încarcă...' : ticket.status === 'WAITING_CLIENT' ? 'Răspunde' : 'Trimite'}
            </Button>
          </div>
        </div>
        )}
      </Card>

      {/* CSAT Survey — shown only when ticket is RESOLVED */}
      {ticket.status === 'RESOLVED' && (
        <Card>
          <CardHeader>
            <h2 className="font-semibold">Chestionar satisfacție</h2>
            <p className="text-sm text-gray-500 dark:text-dark-muted">Ticketul a fost rezolvat. Te rugăm să oferi un feedback și să închizi ticketul.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-dark-text">Scor (1-5 stele)</label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setCsatScore(s)}
                    className="rounded p-1 transition-colors hover:bg-yellow-50 dark:hover:bg-yellow-900/20"
                  >
                    <Star
                      size={28}
                      className={s <= csatScore ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
                    />
                  </button>
                ))}
              </div>
            </div>
            <Textarea
              placeholder="Comentariu (opțional)"
              value={csatComment}
              onChange={(e) => setCsatComment(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button
                disabled={csatScore < 1 || closeWithCsat.isPending}
                onClick={handleCsatSubmit}
              >
                {closeWithCsat.isPending ? 'Se trimite...' : 'Trimite și închide ticketul'}
              </Button>
            </div>
            {currentUser?.id === ticket.createdById && (
              <p className="text-xs text-gray-500 dark:text-dark-muted">
                Nu e rezolvat corect? Bifează „Nu e rezolvat corect" din câmpul de comentariu de mai sus și trimite un comentariu — tichetul se va întoarce în lucru.
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
